// TRAggregate / RDP.Aggregate arguments, checked as LSEG's Excel add-in does.
//
// Pure: no Office.js, no network. planAggregate runs the two checks LSEG makes
// before any request, in LSEG's order (batch 25 [G] agg_both_errors: with no
// Group By and two items, the Group By text wins). The sums themselves are made
// by the interface's TRAggregate formula.
import { LSEG_TEXT } from "../lib/lsegText";
import { flattenFields, scalarText } from "../lib/refinitiv";

export const AGGREGATE_TEXT = {
  NO_GROUPBY: "No groupby defined.",
  TWO_ITEMS: "RDP.Aggregate can only contain one data item",
  // Items separated by a comma outside parentheses ([G] batch25b agg_comma_items).
  COMMA_ITEMS: "Incorrect number of input tables (i.e. items or nested functions) for function 'AGGREGATE'.",
} as const;

export type AggregatePlan = { error: string } | { item: string; groupBy: string };

/**
 * The data item and Group By of an aggregate, or LSEG's error text.
 * - Group By empty or blank: `No groupby defined.` (first).
 * - Items separated by `;` outside parentheses (or in several cells):
 *   `RDP.Aggregate can only contain one data item` ([G] agg_two_items). A `;`
 *   inside parentheses stays in its item ([G] batch25b agg_semicolon_in_params).
 * - Items separated by `,` outside parentheses: `Incorrect number of input
 *   tables ... 'AGGREGATE'.` ([G] batch25b agg_comma_items). With both
 *   separators, the `;` text [A]. A `,` inside parentheses is part of the item.
 * - No data item at all: TR's `No data item defined.` ([G] batch25b agg_no_item).
 * Both come back trimmed, otherwise as typed.
 */
export function planAggregate(dataItem: unknown, groupBy: unknown): AggregatePlan {
  const by = scalarText(groupBy);
  if (!by) return { error: AGGREGATE_TEXT.NO_GROUPBY };
  // `;` first: with both separators the `;` text wins.
  const bySemicolon = flattenFields(dataItem, { keepSemicolonInParens: true, splitOnComma: false });
  if (bySemicolon.length > 1) return { error: AGGREGATE_TEXT.TWO_ITEMS };
  if (!bySemicolon.length) return { error: LSEG_TEXT.NO_DATA_ITEM };
  if (flattenFields(dataItem, { keepSemicolonInParens: true }).length > 1) return { error: AGGREGATE_TEXT.COMMA_ITEMS };
  return { item: bySemicolon[0], groupBy: by };
}
